<template>
  <div>
    <div class="modal fade" id="changeReservationStatusModal" ref="modalEle" tabindex="-1">
      <div class="modal-dialog modal-full-width" role="document">
        <div class="modal-content">

          <div class="modal-header">
            <h5 class="modal-title">Dyspozycja rezerwacji</h5>
            <button type="button" class="close ms-auto" data-bs-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body">

            <div v-if="error" class="alert alert-danger">
              {{ error.message ?? error }}
            </div>

            <table v-if="selectedRecords?.length" class="table table-striped">
              <thead>
                <tr>
                  <th>Nr katalogowy</th>
                  <th>Model</th>
                  <th>Opis</th>
                  <th>Projekt</th>
                  <th>Magazyn</th>
                  <th>Status Mag.</th>
                  <th>Zarezerwowana</th>
                  <th>Wydana</th>
                  <th>Ilość do wydania</th>
                  <th>Komentarz</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="record in selectedRecords" :key="record.id">

                  <td>{{ record.article_catalog_number }}</td>
                  <td>{{ record.article_model }}</td>
                  <td>{{ record.article_description }}</td>
                  <td>{{ record.project_number }}</td>
                  <td>{{ record.required_project_item?.warehouse_received }}</td>
                  <td>{{ inventoryStatuses[record.required_project_item?.inventory_status_id] || '-' }}</td>
                  <td>{{ record.quantity_reserved }}</td>
                  <td>{{ record.fulfilled_quantity || 0 }}</td>

                  <td>
                    <input type="number"  v-if="dispositionsById[record.id]"
                           :step="isIntegerUnit(record.article_unit?.name) ? 1 : 0.01"
                           :min="isIntegerUnit(record.article_unit?.name) ? 1 : 0.01"
                           class="form-control form-control-sm"
                           v-model.number="dispositionsById[record.id].quantity"
                           @input="onQuantityInput(record)"
                           @keydown="blockNonNumeric($event, record.article_unit?.name)">
                  </td>

                  <td>
                    <input type="text"  v-if="dispositionsById[record.id]"
                           class="form-control form-control-sm"
                           v-model="dispositionsById[record.id].comment">
                  </td>

                  <td :class="{ 'alert alert-danger text-danger': record.status === 'niezatwierdzone' }">
                    {{ record.status }}
                  </td>

                </tr>
              </tbody>
            </table>

          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Zamknij
            </button>

            <form @submit.prevent="submitForm">
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                Zapisz
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import { router } from '@inertiajs/vue3';

export default {
  props: {
    selectedRecords: {
      type: Array,
      default: () => []
    },
    statuses: Array
  },

  data() {
    return {
      isSubmitting: false,
      error: null,
      modalObj: null,
      inventoryStatuses: {},
      dispositionsById: {}
    };
  },

  watch: {
    selectedRecords: {
      immediate: true,
      handler(records) {
        this.dispositionsById = {};
        if (!Array.isArray(records)) return;
        records.forEach(record => {
          const fulfilled = parseFloat(record.fulfilled_quantity || 0);
          const reserved = parseFloat(record.quantity_reserved || 0);
          const available = reserved - fulfilled;

          this.dispositionsById[record.id] = {
            id: record.id,
            required_project_item_id: record.required_project_item_id,
            projectId: record.project_id || record.project?.id || null,
            projectNumber: record.project?.number || record.project_number || null,
            quantity: available,
            availableQuantity: available,
            comment: 'Dyspozycja rezerwacji'
          };
        });
        this.loadInventoryStatuses();
      }
    }
  },

  mounted() {
    this.modalObj = new Modal(this.$refs.modalEle);
		  this.$refs.modalEle.addEventListener('hidden.bs.modal', () => {
    this.$emit('clear-selection');
  });
  },

  methods: {

    isIntegerUnit(unitName) {
      if (!unitName) return false;
      return ['szt.', 'szt', 'kpl'].includes(unitName.toLowerCase());
    },

    blockNonNumeric(event, unitName) {
      if (!this.isIntegerUnit(unitName)) return;

      const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

      if (!allowed.includes(event.key) && !/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }

      if (['.', ',', '-'].includes(event.key)) {
        event.preventDefault();
      }
    },

    onQuantityInput(record) {
      const item = this.dispositionsById[record.id];
      if (!item) return;

      if (this.isIntegerUnit(record.article_unit?.name)) {
        let val = parseInt(item.quantity || 0, 10);
        if (isNaN(val)) val = 0;
        item.quantity = val;
      }
    },

    async loadInventoryStatuses() {
      const ids = this.selectedRecords
        .map(r => r.required_project_item?.inventory_status_id)
        .filter(Boolean);

      const unique = [...new Set(ids)];

      if (unique.length === 0) return;

      const response = await axios.get(route('getOrderLists.inventory-status'), {
        params: { statuses_id: unique }
      });

      response.data.forEach(status => {
        this.inventoryStatuses[status.id] = status.name;
      });
    },

    async submitForm() {
      const values = Object.values(this.dispositionsById);

      const invalid = values.find(d =>
        d.quantity < 0.01 || d.quantity > d.availableQuantity
      );

      if (invalid) {
        this.$emit('updated-error', 'Niepoprawne ilości');
        return;
      }

      const payload = values.map(d => ({
        id: d.id,
        required_project_item_id: d.required_project_item_id,
        projectId: d.projectId,
        projectNumber: d.projectNumber,
        quantity: d.quantity,
        availableQuantity: d.availableQuantity,
        comment: d.comment
      }));

      this.isSubmitting = true;

      try {
        await axios.post(route('warehouse-reservations.disposition'), {
          dispositions: payload
        });

        this.$emit('updated-success', 'OK');
        this.$emit('refresh', true);
		this.$emit('clear-selection');

        this.hide();

const ids = [
  ...new Set(
    payload
      .map(item => Number(item.required_project_item_id))
      .filter(Number.isFinite)
  )
];

if (ids.length) {
  const url = route('requiredProjectItems.index', {
    ids: ids.join(',')
  });

  router.get(url);
} else {
  console.warn('❌ BRAK IDS -> NIE MA PRZEKIEROWANIA');
}


      } catch (e) {
        this.error = e.response?.data || e;
        this.$emit('updated-error', this.error.message);
      } finally {
        this.isSubmitting = false;
      }
    },

    show() {
      this.modalObj?.show();
    },

    hide() {
      this.modalObj?.hide();
      // this.dispositionsById = {};
      this.error = null;
	   this.$emit('closed');
    }
  }
};
</script>
